import React from 'react';
import { Button, Tag, Typography } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { OrderOptionDto } from '../dto/order';
import { formatLocalizedQuantity } from '../utils/numberFormatting';
import { formatUnit } from '../utils/units';
import { TRAILER_PAYLOAD_KG } from '../utils/transportCostAllocation';
import { calculateCargoItemsMetrics } from './sharedTransportFormUtils';
import type { CargoTableRow } from './sharedTransportFormRows';
import './TransportSupplierCargoEditor.css';

const { Text } = Typography;
const SWATCHES = 8;

interface Props {
  /** Customer orders on the truck, in route order. */
  orders: OrderOptionDto[];
  rows: CargoTableRow[];
  onRemoveOrder: (orderId: string) => void;
}

/**
 * Owner 2026-10-11: „Co jedzie” of a truck from our warehouse looks like the
 * ZD load of a factory truck (TransportSupplierCargoEditor, read-only) — the
 * trailer, one band per ZK, one line per position. Display only: quantities
 * are edited in „Zmień ilości na tym aucie” (TransportCargoItemCard).
 */
const TransportOrderLoadView: React.FC<Props> = ({ orders, rows, onRemoveOrder }) => {
  const { t, i18n } = useTranslation(['transport', 'orders', 'common']);
  if (orders.length === 0) return null;
  const amount = (value: number | null, unit: string | null | undefined) => (value === null
    ? '—'
    : `${formatLocalizedQuantity(value, i18n.language)} ${formatUnit(unit ?? null, i18n.language)}`);
  const kg = (value: number) => `${formatLocalizedQuantity(Math.round(value), i18n.language)} kg`;
  const swatchOf = new Map(rows.map((row, index) => [row.key, index % SWATCHES]));

  // The trailer: the same weight the form totals (catalog weight × pieces).
  const weights = rows.map((row) => {
    const weight = Number(row.quantity) > 0 ? calculateCargoItemsMetrics([row]).weight : 0;
    return { row, kg: weight > 0 ? weight : null };
  });
  const totalKg = weights.reduce((sum, item) => sum + (item.kg ?? 0), 0);
  const withoutWeight = weights.filter((item) => Number(item.row.quantity) > 0 && item.kg === null).length;
  const scale = Math.max(TRAILER_PAYLOAD_KG, totalKg);

  return (
    <section className="transport-zd-cargo" data-testid="transport-order-load-view">
      <div className="transport-zd-trailer" data-testid="transport-order-load-trailer">
        <div className="transport-zd-trailer__truck" aria-hidden="true">
          <div className="transport-zd-trailer__box">
            {weights.filter((item) => item.kg).map((item) => (
              <span
                key={item.row.key}
                className={`transport-zd-trailer__seg transport-zd-swatch-${swatchOf.get(item.row.key)}`}
                style={{ width: `${(item.kg! / scale) * 100}%` }}
              />
            ))}
          </div>
          <div className="transport-zd-trailer__cab" />
        </div>
        <div className="transport-zd-trailer__text">
          <Text strong className={totalKg > TRAILER_PAYLOAD_KG ? 'transport-zd-trailer__over' : undefined}>
            {t('transport:supplierCargo.trailerWeight', { weight: kg(totalKg), limit: kg(TRAILER_PAYLOAD_KG) })}
          </Text>
          <Text type={totalKg > TRAILER_PAYLOAD_KG ? 'warning' : 'secondary'}>
            {totalKg > TRAILER_PAYLOAD_KG
              ? t('transport:supplierCargo.trailerOver', { weight: kg(totalKg - TRAILER_PAYLOAD_KG) })
              : t('transport:supplierCargo.trailerFree', { weight: kg(TRAILER_PAYLOAD_KG - totalKg) })}
            {withoutWeight > 0 ? ` ${t('transport:supplierCargo.trailerMissingWeight', { count: withoutWeight })}` : ''}
          </Text>
        </div>
      </div>

      {orders.map((order) => {
        const number = order.displayOrderNumber || order.orderNumber;
        const lines = rows.filter((row) => row.orderId === order.id);
        const unit = lines[0]?.unit ?? null;
        const sameUnit = Boolean(unit) && lines.every((row) => row.unit === unit);
        const sum = (pick: (row: CargoTableRow) => number) => lines.reduce((total, row) => total + pick(row), 0);
        return (
          <div key={order.id} className="transport-zd-cargo__group" data-testid={`transport-order-load-${order.id}`}>
            <header className="transport-zd-cargo__band">
              <span className="transport-zd-cargo__zd">{`ZK ${number}`}</span>
              <Tag>{t(`orders:statusOptions.${order.status}`, { defaultValue: t('common:unknownStatus') })}</Tag>
              <div className="transport-zd-cargo__band-text">
                <span className="transport-zd-cargo__supplier">{order.customer?.name || order.customerName || '—'}</span>
                {order.deliveryLocation ? (
                  <span className="transport-zd-cargo__place" title={order.deliveryLocation}>
                    {t('transport:orderLoad.unloadsAt', { place: order.deliveryLocation.split('\n')[0] })}
                  </span>
                ) : null}
              </div>
              {sameUnit && lines.length > 0 ? (
                <span className="transport-zd-cargo__total">
                  {t('transport:supplierCargo.totalOnTruck', {
                    carried: amount(sum((row) => Number(row.quantity) || 0), unit),
                    ordered: amount(sum((row) => Math.max(row.availableQuantity - row.allocatedElsewhere, 0)), unit),
                  })}
                </span>
              ) : null}
              <Button
                type="text"
                size="small"
                icon={<CloseOutlined />}
                className="transport-zd-cargo__remove"
                aria-label={t('transport:orderPicker.remove', { number })}
                onClick={() => onRemoveOrder(order.id)}
              />
            </header>
            {lines.length === 0 ? <Text type="secondary">{t('transport:orderLoad.noLines')}</Text> : null}
            <ul className="transport-zd-cargo__lines">
              {lines.map((row) => {
                const available = Math.max(row.availableQuantity - row.allocatedElsewhere, 0);
                const carried = Number(row.quantity) || 0;
                const isNone = carried <= 0;
                return (
                  <li key={row.key} className={`transport-zd-cargo__line${isNone ? ' is-none' : ''}`} data-testid={`transport-order-load-line-${row.key}`}>
                    <span className={`transport-zd-cargo__swatch transport-zd-swatch-${swatchOf.get(row.key)}`} aria-hidden="true" />
                    <span className="transport-zd-cargo__position">{row.orderLineNumber ?? '—'}</span>
                    <div className="transport-zd-cargo__product">
                      <span className="transport-zd-cargo__name">{row.productName}</span>
                      {row.productDimensions ? <span className="transport-zd-cargo__dims">{row.productDimensions}</span> : null}
                      <span className="transport-zd-cargo__meta">
                        <span>{t('transport:orderLoad.metaOrdered', { amount: amount(row.availableQuantity, row.unit) })}</span>
                        <span>{t('transport:supplierCargo.metaOther', { amount: amount(row.allocatedElsewhere, row.unit) })}</span>
                        <span>{t('transport:orderLoad.metaLeft', { amount: amount(Math.max(available - carried, 0), row.unit) })}</span>
                      </span>
                    </div>
                    <span />
                    <div className="transport-zd-cargo__amount">
                      {isNone
                        ? <Text type="secondary">{t('transport:supplierCargo.notOnThisTruck')}</Text>
                        : <span className="transport-zd-cargo__carried">{amount(carried, row.unit)}</span>}
                      {!isNone && carried < available - 0.0005 ? (
                        <Text type="secondary" className="transport-zd-cargo__of">{t('transport:supplierCargo.ofAvailable', { amount: amount(available, row.unit) })}</Text>
                      ) : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </section>
  );
};

export default TransportOrderLoadView;
